import React, { useEffect, useState } from "react";
import { db, auth } from "../firebase";
import AssetChartContainer from "./AssetChartContainer";
import CompanyShareholders from "./CompanyShareholders";
import MyCompany from "./MyCompany";
import CompanyLogo from "../components/CompanyLogo";
import CompanyVotePanel from "./CompanyVotePanel";
import TakeoverButton from "./TakeoverButton";
import ApplyButton from "./Apply";
import BuySharesModal from "./BuySharesModal"; 
import SellSharesModal from "./SellSharesModal"; 
import TransferSharesModal from "./TransferSharesModal";
import { listenToUserData, getUserData } from "../services/userService";
import FuturesPurchaseModal from "./FuturesModal";
import ContractsOffice from "./contracts/ContractsOffice";
import OptionsHelpModal from "./contracts/OptionsHelpModal";
import { doc, getDoc } from "firebase/firestore";
import AssetProfileView from "./AssetProfileView";

const regionMap = {
  "false": "Global",
  AF: "Africa",
  AS: "Asia",
  AU: "Australia",
  EU: "Europe",
  NA: "North America",
  SA: "South America",
};

export default function CompanyProfilePanel({ 
  company,
  globalVars,
  activeTab,          
  setWindowState,     
  setSelectedCompany,
  onClose,
  addNotification,
  setSelectedPlayer,
}) {
  const [showHelp, setShowHelp] = React.useState(false);
  const [hoverRating, setHoverRating] = useState(0);
  const [companyRating, setCompanyRating] = useState(0);
  const [cooldownActive, setCooldownActive] = useState(false);
  const [showBuyModal, setShowBuyModal] = useState(false);
  const [showSellModal, setShowSellModal] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [userData, setUserData] = useState(null);
  const [showFuturesModal, setShowFuturesModal] = useState(false);
  const TRANSFER_BLOCKED_TICKERS = ["AUD", "BRL", "CNY", "EUR", "ZAR", "BND3", "BND7", "BND14"];
  const FUTURES_BLOCKED_TICKERS = ["BND3", "BND7", "BND14"];
  const [leveragedHoldings, setLeveragedHoldings] = useState(0);
  const isAsset = !!company?.listingId;

  useEffect(() => {
    const fetchLeverage = async () => {
      const userId = auth.currentUser?.uid;
      const ticker = company?.companyTicker;

      if (!userId || !ticker) return;

      try {
        const holderRef = doc(db, "Companies", ticker, "Holders", userId);
        const holderSnap = await getDoc(holderRef);

        if (holderSnap.exists()) {
          setLeveragedHoldings(holderSnap.data().holderHoldingsLeveraged || 0);
        } else {
          setLeveragedHoldings(0);
        }
      } catch (err) {
        console.error("Error fetching leverage from subcollection:", err);
        setLeveragedHoldings(0);
      }
    };

    fetchLeverage();
  }, [company?.companyTicker]);

  useEffect(() => {
    const uid = auth.currentUser?.uid;
    if (!uid) return;
    const unsubscribe = listenToUserData(uid, setUserData);
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    setCompanyRating(Number(company?.companyRating) || 0);
    
    setShowBuyModal(false);
    setShowSellModal(false);
  }, [company]);

  const handleTabClick = (tabName) => {
    setShowBuyModal(false);
    setShowSellModal(false);
    setShowTransferModal(false);
    setShowFuturesModal(false);

    if (tabName === "My Company" || tabName === "Contracts") {
      setSelectedCompany(null); 
    }
    setWindowState(tabName); 
  };
  
  const handleRatingClick = async (newRating) => {
    if (userData?.userRank < 4) {
      addNotification("Your title must be Day Trader or higher to submit company ratings.", "info");
      return;
    }

    if (cooldownActive || !company?.companyTicker) return;

    try {
      const ticker = company.companyTicker;
      const res = await fetch(
        "https://us-central1-stockmarket-83a73.cloudfunctions.net/submitCompanyRating",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${await auth.currentUser.getIdToken()}`,
          },
          body: JSON.stringify({ ticker, rating: Number(newRating) }),
        }
      );

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to submit rating");

      setCompanyRating(data.average || newRating);
      addNotification("Company rating submitted.");
      setCooldownActive(true);
      setTimeout(() => setCooldownActive(false), 20000);
    } catch (err) {
      console.error("Error submitting rating:", err);
      addNotification("Error submitting rating.", "error");
    }
  };

  const renderStars = () => (
    <span style={{ display: "inline-flex", gap: "4px" }}>
      {[1, 2, 3, 4, 5].map((star) => {
        const isLit = hoverRating >= star || (!hoverRating && companyRating >= star);
        return (
          <span
            key={star}
            onMouseEnter={() => !cooldownActive && setHoverRating(star)}
            onMouseLeave={() => !cooldownActive && setHoverRating(0)}
            onClick={() => !cooldownActive && handleRatingClick(star)}
            style={{
              cursor: cooldownActive ? "not-allowed" : "pointer",
              fontSize: "1.4rem",
              color: isLit ? "#50e3c2" : "#0a4038",
              textShadow: isLit ? "0 0 6px rgba(80,227,194,0.8)" : "none",
              transition: "color 0.2s, text-shadow 0.2s",
            }}
          >
            ★
          </span>
        );
      })}
    </span>
  );

  if (!company) {
    return (
      <section 
        id="company-panel" 
        className="tabbed-panel" 
        style={{ 
          display: 'flex', 
          flexDirection: 'column',
          height: '100%',
          width: '99%', 
          margin: 0,
          padding: 0,
          justifyContent: 'flex-start',
          background: 'rgba(13, 22, 21, 0.95)', 
          backdropFilter: 'blur(12px)',
          border: '1px solid rgba(80, 227, 194, 0.2)',
          borderRadius: '8px',
          overflow: 'hidden',      
          position: 'relative'
        }}
      >
        <div className="tab-navigation-container" style={{ 
          flexShrink: 0, 
          borderBottom: '1px solid rgba(80, 227, 194, 0.2)', 
          background: 'rgba(255, 255, 255, 0.02)',
          height: '45px',
          width: '100%',
          overflow: 'hidden'
        }}>
          <div className="nav-modules-row" style={{ 
            display: 'flex', 
            flexDirection: 'row', 
            overflowX: 'auto', 
            alignItems: 'stretch',
            height: '100%',
            scrollbarWidth: 'none'
          }}>
            {["Company Profile", "Company Charts", "Company Shareholders", "My Company", "Contracts"]
              .filter((tab) => {
                if (tab === "Contracts") {
                  return (userData?.userRank ?? 0) >= 8;
                }
                return true;
              })
              .map((tab) => (
                <div 
                  key={tab} 
                  className={`nav-module ${activeTab === tab ? "active" : ""}`} 
                  onClick={() => handleTabClick(tab)}
                  style={{
                    padding: '0 15px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                    boxSizing: 'border-box',
                    borderBottom: activeTab === tab ? '2px solid #50E3C2' : '2px solid transparent',
                    transition: 'all 0.2s ease',
                    whiteSpace: 'nowrap',
                  }}
                >
                  <span 
                    className="module-label" 
                    style={{ 
                      fontFamily: 'JetBrains Mono', 
                      fontSize: '0.65rem',
                      color: activeTab === tab ? '#50E3C2' : '#8b949e',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      lineHeight: '1' 
                    }}
                  >
                    {tab.replace("Company ", "")}
                  </span>
                </div>
            ))}
          </div>
        </div>

        <div 
          className="tab-content" 
          key={activeTab}
          style={{ 
            display: "flex",
            flexDirection: "column",
            background: "rgba(0, 5, 5, 0.2)",
            border: "1px solid rgba(80, 227, 194, 0.1)",
          }}
        >
          {activeTab === "Company Profile" && <p className="loading-text">No company selected.</p>}
          {activeTab === "Company Charts" && <p className="loading-text">No company selected.</p>}
          {activeTab === "Company Shareholders" && <p className="loading-text">No company selected.</p>}
          {activeTab === "My Company" && <MyCompany />}
          {activeTab === "Contracts" && (
            userData?.userRank >= 8 ? (
              <ContractsOffice userData={userData} />
            ) : (
              <div className="locked-office-overlay">
                <h2>CONTRACTS</h2>
                <p>Locked</p>
              </div>
            )
          )}
        </div>
      </section>
    );
  }

  let issuedType = "Stock";
  if (company.companyCrypto) issuedType = "Cryptocurrency";
  else if (company.companyCurrency) issuedType = "Currency";
  else if (company.companyTicker === "XAU") issuedType = "Gold Unit";
  else if (["BND3", "BND7", "BND14"].includes(company.companyTicker)) issuedType = "Bond";
  else if (company.companyTicker === "INDX") issuedType = "Index Fund";

  const totalVol = company.companyStockVolume || 1;
  const majorityPercent = company.companyMajorityShares
    ? ((company.companyMajorityShares / totalVol) * 100).toFixed(2)
    : "0.00";

  const availablePercent = totalVol
    ? ((company.companyOutstanding / totalVol) * 100).toFixed(2)
    : "0.00";

  const displayHoldings = userData
    ? company.companyTicker === "XAU"
      ? userData.userGold || 0
      : userData[`company${company.companyTicker}`] || 0
    : 0;

  const heldValue = displayHoldings * company.companyStockPrice;
  const FOREX_TICKERS = ['AUD', 'BRL', 'CNY', 'EUR', 'ZAR'];
  const isForex = FOREX_TICKERS.includes(company?.companyTicker);
  const isLocked = userData?.userRank < 9 || FUTURES_BLOCKED_TICKERS.includes(company?.companyTicker);
  const isTransferLocked = userData?.userRank < 7 || !displayHoldings || TRANSFER_BLOCKED_TICKERS.includes(company?.companyTicker);
  const isForexLocked = isForex && userData?.userRank < 6;

  return (
    <section 
      id="company-panel-v3" 
      style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        justifyContent: 'flex-start', 
        alignItems: 'stretch',
        height: '100%',
        width: '100%',
        margin: 0,
        padding: 0,
        overflow: 'hidden',
        position: 'relative',
        background: 'rgba(13, 22, 21, 0.95)',
        backdropFilter: 'blur(12px)',
      }}
    >
      <style>
        {`
          .nav-modules-row::-webkit-scrollbar { display: none; }
          .scrollable-content::-webkit-scrollbar { width: 4px; }
          .scrollable-content::-webkit-scrollbar-thumb { background: rgba(80, 227, 194, 0.2); }
        `}
      </style>
      
      <button 
        onClick={onClose} 
        style={{ 
          position: 'absolute', 
          top: '50px', 
          right: '5px', 
          zIndex: 100,
          background: 'none',
          border: 'none',
          color: '#50E3C2',
          fontSize: '24px',
          cursor: 'pointer'
        }}
      >
        ×
      </button>
      
      <div 
        className="tab-navigation-container" 
        style={{ 
          flexShrink: 0, 
          borderBottom: '1px solid rgba(80, 227, 194, 0.2)', 
          background: 'rgba(255, 255, 255, 0.02)',
          height: '45px',
          width: '100%'
        }}
      >
        <div 
          className="nav-modules-row" 
          style={{ 
            display: 'flex', 
            flexDirection: 'row', 
            overflowX: 'auto', 
            alignItems: 'stretch',
            height: '100%',
            msOverflowStyle: 'none',
            scrollbarWidth: 'none',
            WebkitOverflowScrolling: 'touch', 
          }}
        >
          {["Company Profile", "Company Charts", "Company Shareholders", "My Company", "Contracts"]
            .filter((tab) => {
              if (tab === "Contracts") {
                return (userData?.userRank ?? 0) >= 8;
              }
              return true;
            })
            .map((tab) => (
              <div 
                key={tab} 
                className={`nav-module ${activeTab === tab ? "active" : ""}`} 
                onClick={() => handleTabClick(tab)}
                style={{
                  padding: '0 15px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                  boxSizing: 'border-box',
                  borderBottom: activeTab === tab ? '2px solid #50E3C2' : '2px solid transparent',
                  transition: 'all 0.2s ease',
                  whiteSpace: 'nowrap',
                }}
              >
                <span 
                  className="module-label" 
                  style={{ 
                    fontFamily: 'JetBrains Mono', 
                    fontSize: '0.65rem',
                    color: activeTab === tab ? '#50E3C2' : '#8b949e',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    lineHeight: '1'
                  }}
                >
                  {tab.replace("Company ", "")}
                </span>
              </div>
            ))}
        </div>
      </div>

      <div 
        className="scrollable-content" 
        style={{ 
          flex: 1, 
          overflowY: "auto", 
          padding: "15px",
          display: "flex", 
          flexDirection: "column"
        }}
      >

        {activeTab === "Company Profile" && company && (
          isAsset ? (
            <AssetProfileView selectedAsset={company} />
          ) : (
            <>
              <div style={{ display: 'flex', gap: '20px', marginBottom: '15px', alignItems: 'center' }}>
                <div className="company-logo" style={{ 
                  background: 'rgba(255,255,255,0.02)', 
                  padding: '10px', 
                  borderRadius: '4px',
                  border: '1px solid rgba(255,255,255,0.05)'
                }}>
                  <CompanyLogo
                    accent={company.companyLogoAccent}
                    accentColor={company.companyLogoAccentColor}
                    base={company.companyLogoBase}
                    baseColor={company.companyLogoBaseColor}
                    symbol={company.companyLogoSymbol}
                    symbolColor={company.companyLogoSymbolColor}
                    size={110}
                  />
                </div>
                <div>
                  <h3 style={{ 
                    fontFamily: 'JetBrains Mono', 
                    fontSize: '1rem', 
                    color: '#50E3C2', 
                    margin: 0,
                    borderLeft: '3px solid #50E3C2',
                    paddingLeft: '12px'
                  }}>
                    {company.companyName.toUpperCase()}
                  </h3>
                  <p style={{ fontFamily: 'JetBrains Mono', fontSize: '0.75rem', color: '#8b949e', margin: '5px 0 0 12px' }}>
                    {company.companyTicker} | {regionMap[company.companyRegion] || "GLOBAL_SEC"}
                  </p>
                </div>
              </div>

              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: '1fr 1fr', 
                gap: '0 25px', 
                marginBottom: '15px',
                borderTop: '1px solid rgba(80, 227, 194, 0.1)',
                paddingTop: '12px'
              }}>
                {[
                  ["Ticker", company.companyTicker],
                  ["CEO", company.companyCEO !== "NTC" ? company.companyCEO : "NTC"],
                  ["Employees", company.companyMemberCount > 0 ? company.companyMemberCount : "0"],
                  ["Majority", `${company.companyMajorityShareholder} (${majorityPercent}%)`],
                  ["Issued", issuedType],
                  ["Total Vol", company.companyStockVolume?.toLocaleString() ?? "N/A"],
                  ["Available", `${company.companyOutstanding?.toLocaleString() ?? "0"} (${availablePercent}%)`],
                  ["Price", company.companyStockPrice ? `$${company.companyStockPrice.toLocaleString()}` : "N/A"],
                  ["Market Cap", company.companyStockVolume && company.companyStockPrice ? `$${(company.companyStockVolume * company.companyStockPrice).toLocaleString()}` : "N/A"],
                  ["Free Float", company.companyOutstanding && company.companyStockPrice ? `$${(company.companyOutstanding * company.companyStockPrice).toLocaleString()}` : "N/A"],
                  ["Held", displayHoldings > 0 ? displayHoldings.toLocaleString() : null],
                  ["Value", heldValue > 0 ? `$${heldValue.toLocaleString()}` : null],
                  ["Leveraged", leveragedHoldings > 0 ? leveragedHoldings.toLocaleString() : null],
                  ["Rating", renderStars()]
                ].map(([label, value]) => value !== null && (
                  <div key={label} style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    padding: '5px 0', 
                    borderBottom: '1px solid rgba(255,255,255,0.03)',
                    fontFamily: 'JetBrains Mono',
                    fontSize: '0.7rem'
                  }}>
                    <span style={{ color: '#50E3C2', fontWeight: 'bold' }}>{label}:</span>
                    <span style={{ color: '#FFF', textAlign: 'right' }}>{value}</span>
                  </div>
                ))}
              </div>

              <CompanyVotePanel ticker={company.companyTicker} />

              <div className="button-row" style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(6, 1fr)', 
                gap: '4px',
                marginTop: '15px',
                alignItems: 'stretch',
                width: '100%',
                minWidth: '0', 
                boxSizing: 'border-box'
              }}>
                <button 
                  className={`jetbrains-btn ${isForexLocked ? "disabled" : ""}`} 
                  disabled={isForexLocked}
                  style={{ fontSize: '0.55rem', padding: '8px 2px', width: '100%', opacity: isForexLocked ? 0.4 : 1, cursor: isForexLocked ? 'not-allowed' : 'pointer' }}
                  onClick={() => { setSelectedCompany(company); setShowBuyModal(true); }}
                >
                  BUY
                </button>

                <button
                  className={`jetbrains-btn ${(displayHoldings <= 0 || isForexLocked) ? "disabled" : ""}`}
                  disabled={displayHoldings <= 0 || isForexLocked}
                  style={{ fontSize: '0.6rem', padding: '8px 2px', width: '100%', opacity: (displayHoldings <= 0 || isForexLocked) ? 0.3 : 1, cursor: (displayHoldings <= 0 || isForexLocked) ? 'not-allowed' : 'pointer' }}
                  onClick={() => { if (displayHoldings > 0) { setSelectedCompany(company); setShowSellModal(true); }}}
                >
                  SELL
                </button>

                <button 
                  className={`jetbrains-btn ${isTransferLocked ? "disabled" : ""}`}
                  disabled={isTransferLocked}
                  style={{ fontSize: '0.6rem', padding: '8px 2px', width: '100%', opacity: isTransferLocked ? 0.3 : 1, cursor: isTransferLocked ? 'not-allowed' : 'pointer' }}
                  onClick={() => { setSelectedCompany(company); setShowTransferModal(true); }}
                >
                  TRANSFER
                </button>

                <button 
                  className={`jetbrains-btn ${isLocked ? "disabled" : ""}`}
                  disabled={isLocked}
                  style={{ fontSize: '0.6rem', padding: '8px 2px', width: '100%', opacity: isLocked ? 0.3 : 1, cursor: isLocked ? 'not-allowed' : 'pointer' }}
                  onClick={() => { setSelectedCompany(company); setShowFuturesModal(true); }}
                >
                  FUTURES
                </button>
                
                {company.companyMemberCount > 0 && company.companyCEO !== "NTC" ? (
                  <div style={{ display: 'flex' }}>
                     <ApplyButton company={company} style={{ fontSize: '0.6rem', padding: '8px 2px', width: '100%' }} />
                  </div>
                ) : <div />}
                
                <div style={{ display: 'flex' }}>
                   <TakeoverButton companyData={company} style={{ fontSize: '0.6rem', padding: '8px 2px', width: '100%' }} />
                </div>
              </div>

              {company.companyDescription && (
                <div className="terminal-card" style={{ 
                  marginTop: "15px", 
                  padding: '10px', 
                  fontSize: '0.7rem', 
                  color: 'rgba(255,255,255,1.0)',
                  background: 'rgba(0,0,0,0.2)',
                  border: '1px solid rgba(255,255,255,0.05)',
                  lineHeight: '1.4'
                }}>
                  {company.companyDescription}
                </div>
              )}
            </>
          )
        )}

        {/* OTHER TABS */}
        {activeTab === "Company Charts" && company && (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: '450px' }}>
            <AssetChartContainer 
              selectionId={company.companyTicker || company.itemId} 
              currentUser={auth.currentUser} 
            />
          </div>
        )}
        
        {activeTab === "Company Shareholders" && company && (
          <CompanyShareholders ticker={company.companyTicker} addNotification={addNotification} onSelectedPlayer={setSelectedPlayer}/>
        )}
        
        {activeTab === "My Company" && (
          <div style={{ width: '100%', boxSizing: 'border-box' }}>
            <MyCompany userId={auth.currentUser?.uid} />
          </div>
        )}

        {activeTab === "Contracts" && (
          userData?.userRank >= 8 ? (
            <ContractsOffice userData={userData} />
          ) : (
            <div className="locked-office-overlay">
              <h2>CONTRACTS</h2>
              <p>Locked</p>
            </div>
          )
        )}
      </div>

      {(showBuyModal && activeTab === "Company Profile" && company && userData && !isAsset) && (
        <BuySharesModal 
          company={company} 
          globalVars={globalVars}
          userData={userData} 
          onClose={() => setShowBuyModal(false)} 
        />
      )}
      {showSellModal && company && userData && (
        <SellSharesModal company={company} userData={userData} onClose={() => setShowSellModal(false)} />
      )}
      {showTransferModal && company && userData && (
        <TransferSharesModal company={company} userData={userData} onClose={() => setShowTransferModal(false)} refreshUserData={async () => { const updated = await getUserData(auth.currentUser.uid); setUserData(updated); }} />
      )}
      {showFuturesModal && company?.companyStockPrice != null && userData && (
        <FuturesPurchaseModal companyStockPrice={company.companyStockPrice} userRank={userData.userRank} userState={userData} companyState={company} onClose={() => setShowFuturesModal(false)} onConfirm={() => setShowFuturesModal(false)} />
      )}
      {showHelp && <OptionsHelpModal onClose={() => setShowHelp(false)} />}
    </section>
  );
}